import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, ArrowLeft, ArrowRight, ExternalLink, Image as ImageIcon, FileText, Trophy, Loader2, Flag, Megaphone, ScrollText } from 'lucide-react';
import { getExperience, getExperiences, postInscription, resolveImg } from '../lib/api';
import { useToast } from '../hooks/use-toast';

import { useLang } from '../lib/i18n';
const formatLong = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const CATEGORY_META = {
  cartel: { icon: Megaphone, label: 'Cartel' },
  reglamento: { icon: ScrollText, label: 'Reglamento' },
  tee_times: { icon: Flag, label: 'Tee Times' },
  clasificaciones: { icon: Trophy, label: 'Clasificaciones' },
  album: { icon: ImageIcon, label: 'Álbum' },
  horarios: { icon: Clock, label: 'Horarios' },
  otros: { icon: FileText, label: 'Documento' },
};

export default function ExperienciaDetalle() {
  const { ui } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [exp, setExp] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', handicap: '', notas: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([getExperience(id), getExperiences()])
      .then(([e, list]) => { setExp(e); setAll(list); })
      .catch(() => navigate('/experiencias'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#c9a063]"><Loader2 size={32} className="animate-spin" /></div>
    );
  }
  if (!exp) return null;

  const idx = all.findIndex((e) => e.id === id);
  const prev = all[idx - 1];
  const next = all[idx + 1];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postInscription({ ...form, experience_id: exp.id });
      toast({ title: 'Inscripción enviada', description: `Hemos recibido tu solicitud para ${exp.title}. Te contactaremos en breve.` });
      setForm({ nombre: '', email: '', telefono: '', handicap: '', notas: '' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo enviar la inscripción. Inténtalo de nuevo.', variant: 'destructive' });
    }
    setSending(false);
  };

  const attachmentsByCat = (exp.attachments || []).reduce((acc, a) => {
    const k = a.category || 'otros';
    acc[k] = acc[k] || [];
    acc[k].push(a);
    return acc;
  }, {});

  return (
    <div>
      <section className="relative h-[70vh] min-h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-[#1a1a1d]">
          {exp.cover && <img src={resolveImg(exp.cover)} alt={exp.title} className="w-full h-full object-cover" />}
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.3) 0%, rgba(13,13,14,0.7) 60%, rgba(13,13,14,1))' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pb-14 w-full">
          <Link to="/experiencias" className="inline-flex items-center gap-2 text-white/70 hover:text-[#c9a063] text-[12px] uppercase tracking-[0.25em] mb-6 transition-colors"><ArrowLeft size={14} /> {ui('btn.back_to_calendar')}</Link>
          <div className="text-[#c9a063] gd-eyebrow mb-3">{exp.month} · {exp.region}</div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-white leading-tight max-w-4xl">{exp.title}</h1>
          {exp.subtitle && <p className="font-script text-[#c9a063] text-3xl md:text-4xl mt-3">{exp.subtitle}</p>}
          <div className="flex flex-wrap items-center gap-6 mt-6 text-white/80">
            <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-[#c9a063]" /> <span className="capitalize">{formatLong(exp.date)}</span></span>
            <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-[#c9a063]" /> {exp.venue}</span>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-10 bg-[#0d0d0e]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <div className="gd-eyebrow mb-4">{ui('exp.experience')}</div>
              <p className="text-white/75 text-lg leading-relaxed whitespace-pre-line">{exp.description}</p>
            </div>

            {exp.schedule && exp.schedule.length > 0 && (
              <div>
                <div className="gd-eyebrow mb-6 flex items-center gap-2"><Clock size={14} /> {ui('exp.schedule')}</div>
                <div className="space-y-3">
                  {exp.schedule.map((s, i) => (
                    <div key={i} className="flex items-center gap-6 border-l-2 border-[#c9a063]/40 pl-5 py-2">
                      <div className="font-serif-display text-2xl text-[#c9a063] w-24">{s.time}</div>
                      <div className="text-white/80 text-lg">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {exp.attachments && exp.attachments.filter((a) => a.url && a.url.trim()).length > 0 && (
              <div>
                <div className="gd-eyebrow mb-6">{ui('exp.contents')}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exp.attachments.filter((a) => a.url && a.url.trim()).map((a) => {
                    const meta = CATEGORY_META[a.category] || CATEGORY_META.otros;
                    const Icon = a.type === 'image' ? ImageIcon : a.type === 'pdf' ? FileText : meta.icon;
                    const defaultLabel = a.type === 'image' ? 'Ver imagen' : a.type === 'pdf' ? 'Descargar PDF' : 'Ver enlace';
                    const displayLabel = (a.label && a.label.trim()) || defaultLabel;
                    const showCategory = a.category && a.category !== 'otros';
                    return (
                      <a key={a.id} href={a.type === 'link' ? a.url : resolveImg(a.url)} target="_blank" rel="noreferrer" className="gd-card p-5 flex flex-col items-start gap-3 hover:no-underline" data-testid={`experience-attachment-${a.id}`}>
                        <div className="w-10 h-10 border border-[#c9a063]/40 flex items-center justify-center text-[#c9a063]"><Icon size={16} /></div>
                        <div className="text-white font-serif-display text-lg">{displayLabel}</div>
                        {showCategory && <div className="text-white/55 text-sm uppercase tracking-wider text-[10px]">{meta.label}</div>}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {exp.links && exp.links.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {exp.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer" className={`${l.primary ? 'gd-btn-gold' : 'gd-btn-ghost'} px-7 py-3 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium inline-flex items-center gap-2`}>
                    {l.label} <ExternalLink size={12} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="gd-card p-7 lg:sticky lg:top-32">
              {exp.show_inscription !== false ? (
                <>
                  <div className="font-script text-[#c9a063] text-3xl mb-1">{ui('exp.registration')}</div>
                  <h3 className="font-serif-display text-2xl text-white mb-5">{ui('exp.sign_up')}</h3>
                  <form onSubmit={submit} className="space-y-3">
                    <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder={ui('form.name')} className="w-full bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none" />
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={ui('form.email')} className="w-full bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none" />
                    <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder={ui('form.phone')} className="w-full bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none" />
                    <input value={form.handicap} onChange={(e) => setForm({ ...form, handicap: e.target.value })} placeholder={ui('form.handicap')} className="w-full bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none" />
                    <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder={ui('form.notes')} className="w-full bg-transparent border border-white/15 text-white placeholder-white/40 px-3 py-2.5 text-sm focus:border-[#c9a063] focus:outline-none resize-none" />
                    <button disabled={sending} type="submit" className="gd-btn-gold w-full py-3 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium disabled:opacity-60">{sending ? 'Enviando...' : 'Enviar inscripción'}</button>
                    <p className="text-white/40 text-xs text-center mt-3">{ui('exp.confirm_contact')}</p>
                  </form>
                </>
              ) : (
                <>
                  <div className="font-script text-[#c9a063] text-3xl mb-1">¿Te interesa?</div>
                  <h3 className="font-serif-display text-2xl text-white mb-5">{ui('exp.event_info')}</h3>
                  <p className="text-white/70 text-[15px] leading-relaxed whitespace-pre-line mb-6">{exp.inscription_text || 'Para más información, contáctanos directamente.'}</p>
                  {exp.inscription_url ? (
                    <a href={exp.inscription_url} target="_blank" rel="noreferrer" className="gd-btn-gold inline-flex items-center justify-center gap-2 w-full py-3 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium">
                      Más información <ExternalLink size={12} />
                    </a>
                  ) : (
                    <Link to="/contacto" className="gd-btn-gold inline-flex items-center justify-center gap-2 w-full py-3 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium">
                      Contactar
                    </Link>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>

        <div className="max-w-6xl mx-auto mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
          {prev ? (
            <Link to={`/experiencias/${prev.id}`} className="gd-card p-6 group">
              <div className="text-[#c9a063] gd-eyebrow mb-2 inline-flex items-center gap-2"><ArrowLeft size={12} /> {ui('btn.previous')}</div>
              <h4 className="font-serif-display text-xl text-white group-hover:text-[#c9a063] transition-colors">{prev.title}</h4>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/experiencias/${next.id}`} className="gd-card p-6 group text-right">
              <div className="text-[#c9a063] gd-eyebrow mb-2 inline-flex items-center gap-2 justify-end">{ui('btn.next')} <ArrowRight size={12} /></div>
              <h4 className="font-serif-display text-xl text-white group-hover:text-[#c9a063] transition-colors">{next.title}</h4>
            </Link>
          ) : <div />}
        </div>
      </section>
    </div>
  );
}
